import { useTranslation } from 'react-i18next';
import { ComponentProps, useCallback } from 'react';

import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { useOpMutation } from 'utils/customHooks/useOpMutation';
import { OpForm } from 'new-components/DLS/OpForm/OpForm';

import './RequestAddQuantityModal.scss';

type RequestAddQuantityModal = Omit<
  ComponentProps<typeof OpModal>,
  'onOk' | 'onCancel' | 'okText' | 'title'
> & {
  orgId: number;
  packageId?: number;
  featureName: string;
  onClose: () => void;
};

export const RequestAddQuantityModal = ({
  onClose: closeCallback,
  orgId,
  packageId,
  featureName: feature,
  ...modalProps
}: RequestAddQuantityModal) => {
  const { t } = useTranslation();

  const { mutate } = useOpMutation({
    apiEndpointName: 'createRequest',
    onSuccessCallback: closeCallback,
  });

  const handleSubmit = useCallback(
    (args: { values: { quantity: number } }) => {
      mutate({
        apiEndpointRequirements: [String(orgId)],
        payload: {
          requestType: 'Q',
          requestedQuantity: args.values.quantity,
          packageId,
        },
      });
    },
    [mutate, orgId, packageId],
  );

  const [form] = OpForm.useForm();

  return (
    <OpModal
      destroyOnHidden
      onOk={form?.submit}
      onCancel={closeCallback}
      okText={t('Request licenses')}
      title={t('Request licenses')}
      {...modalProps}
    >
      <OpForm<{ quantity: number }>
        hasError={false}
        isReadOnly={false}
        form={form}
        defaultButtons={false}
        onSubmit={handleSubmit}
      >
        <OpForm.InputNumber
          autoFocus
          label={t('How many {{feature}} licenses do you need?', { feature })}
          name="quantity"
          className="request-add-quantity-modal quantity-input"
          min={1}
          rules={[
            { required: true },
            {
              type: 'number',
              min: 1,
              message: t('Must submit a quantity of at least 1'),
            },
          ]}
        />
      </OpForm>
    </OpModal>
  );
};
